import { config } from 'config';
import i18n from 'i18nextInstance';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
const {
    availableLanguages,
    hideStatistics,
    patientIdentificationAlpha,
    sysAdmins
} = config;

export const checkPasswordSecurity = password => {
    const checks = {
        lowerCase: !!/[a-z]/.test(password),
        minLength: password.length >= 8,
        number: !!/[0-9]/.test(password),
        specialChar: !!/[!@#$%^&*(),.?":{}|<>]/.test(password),
        upperCase: !!/[A-Z]/.test(password)
    };
    const isValid = Object.values(checks).every(value => Boolean(value));
    return { checks, isValid };
};

export const clearObject = (object = {}, clearFalses = false) => {
    const newObject = {};
    Object.keys(object).forEach(key => {
        const value = object[key];
        if (value === undefined || value === null || (clearFalses && !value)) return;
        newObject[key] = value;
    });
    return newObject;
};

export const dateToObject = date => ({
    day: date.date().toString().padStart(2, '0'),
    month: (date.month() + 1).toString().padStart(2, '0'),
    year: date.year().toString()
});

export const fixedPercentage = value => value !== 0 ? `${ value.toFixed(2) }%` : '0%';

export const formatDate = string => !!string ? moment(string).format('DD/MM/YYYY') : '';

export const formatOptions = (options, label = 'label', value = 'value') =>
    options
        ? options.map(option => ({ label: option[label], value: option[value] }))
        : [];

export const getAdditionalValues = additional => {
    const object = {};
    const pairs = additional.split('|');
    pairs.forEach(pair => {
        const [key, value] = pair.split(':');
        object[key] = value;
    });
    return object;
};

export const getHideStatistics = () => ({
    absenteeism: true,
    administration: false,
    diagnostics: false,
    general: true,
    licenses: true,
    preworks: false,
    professionals: false,
    recipes: false,
    recommendations: false,
    sessions: false,
    statistics: false,
    surveys: false,
    symptoms: false,
    turns: true,
    videocall: false,
    ...hideStatistics
});

export const getPaymentStatus = paymentStatus => {
    switch (paymentStatus) {
    case ('coverage'): return i18n.t('Cobertura');
    case ('completed'): return i18n.t('Pago');
    case ('completed_by_admin'): return i18n.t('Administrador');
    case ('completed_by_doctor'): return i18n.t('Profesional');
    case ('notrequired'): return i18n.t('No requerido');
    default: return i18n.t('Sin procesar');
    }
};

export const getRangeArray = (start, end, { increase = 1, reverse = false }) => {
    const result = [];
    for (let i = start;i <= end;i = i + increase) {
        result.push(i);
    }
    return reverse ? result.reverse() : result;
};

export const getTurnStatus = status => {
    switch (status) {
    case ('cancelled'): return i18n.t('Cancelado');
    case ('finalized'): return i18n.t('Atendido');
    case ('initial'): return i18n.t('Pendiente');
    case ('on course'): return i18n.t('En curso');
    default: return '-';
    }
};

export const identificationFormat = identification => identification ? (!patientIdentificationAlpha ? identification.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.') : identification) : '';

export const isAlphanumeric = character => !!character.match('[a-zA-Z0-9]');

export const isDocFile = type => ['application/pdf'].includes(type);

export const isImageFile = type => [
    'image/bmp',
    'image/gif',
    'image/jpeg',
    'image/jpg',
    'image/png'
].includes(type);

export const isLocked = (user, action) => {
    if (!user?.restricted_access) return false;
    return user?.locked_actions?.includes(action);
};

export const isSuperadmin = mail => sysAdmins.includes(mail);

export const minutesFormat = (seconds = 0, format = 'default') => {
    const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const mh = Math.floor(seconds % 3600 / 60).toString().padStart(2, '0');
    const s = parseInt(seconds % 60, 10).toString().padStart(2, '0');
    switch (format) {
    case ('colon'): {
        return `${ m }:${ s }`;
    }
    case ('default'): {
        return `${ m }' ${ s }"`;
    }
    case ('hours'): {
        return `${ h }:${ mh }:${ s }`;
    }
    default: {
        return `${ m }' ${ s }"`;
    }
    }
};

export const objectShrink = (object = {}, elements = []) => {
    const result = {};
    elements.forEach(key => result[key] = object[key]);
    return result;
};

export const objectToArray = object => (
    Object.keys(object).map(key => ({
        key,
        value: object[key]
    }))
);

export const objectToDate = ({ day, month, year }) => (day && month && year) ? moment(`${ year }-${ month }-${ day }`, 'YYYY-MM-DD') : null;

export const objectToChart = (object = {}, accessor) => Object.keys(object).map(key => ({
    key,
    label: accessor ? accessor(key) : key,
    value: object[key]
}));

export const overlapTime = (from1, to1, from2, to2) =>
    (from2 >= from1 && from2 < to1)
    || (to2 <= to1 && to2 > from1)
    || (from2 <= from1 && to2 >= to1);

export const parseAbsenceReportStatus = status => {
    switch (status) {
    case ('accepted'): return i18n.t('Aceptado');
    case ('deleted'): return i18n.t('Eliminado');
    case ('oncourse'): return i18n.t('En proceso');
    case ('pending'): return i18n.t('Pendiente');
    case ('rejected'): return i18n.t('Rechazado');
    case ('reviewed'): return i18n.t('En revisión');
    default: return i18n.t('Desconocido');
    }
};

export const parseAppointmentStatusLabel = label => {
    switch (label) {
    case ('absent'): return i18n.t('Paciente ausente');
    case ('admitted'): return i18n.t('Paciente admitido');
    case ('cancelled'): return i18n.t('Turno cancelado');
    case ('finalized'): return i18n.t('Turno atendido');
    case ('initial'): return i18n.t('Turno pendiente');
    case ('inroom'): return i18n.t('Paciente en sala');
    case ('oncourse'): return i18n.t('Turno en curso');
    default: return '';
    }
};

export const parseAppointmentType = type => {
    switch (type) {
    case ('presential'): return i18n.t('Presencial');
    case ('online'): return i18n.t('Online');
    default: return 'n/d';
    }
};

export const parseBool = value => !!value ? i18n.t('Si') : i18n.t('No');

export const parseCountryLanguage = language => {
    switch (language) {
    case ('es'): return ('ES');
    case ('en'): return ('US');
    }
};

export const parseDiagnosis = array => {
    if (isEmpty(array)) return '';
    return array.map(({ diagnoses_term }) => diagnoses_term).join(', ');
};

export const parseDiagnosisCode = array => {
    if (isEmpty(array)) return '';
    return array.map(({ diagnoses_conceptId }) => diagnoses_conceptId).join(', ');
};

export const parseDiagnosticStatus = status => {
    switch (status) {
    case ('confirmed'): return i18n.t('Confirmado');
    case ('presumptive'): return i18n.t('Presuntivo');
    default: return '-';
    }
};

export const parseDiagnosticType = type => {
    switch (type) {
    case ('edition'): return i18n.t('Edición');
    case ('new_attention'): return i18n.t('Nueva atención');
    case ('original'): return i18n.t('Original');
    default: return '-';
    }
};

export const parseGender = gender => {
    switch (gender) {
    case ('Male'): return i18n.t('Masculino');
    case ('Female'): return i18n.t('Femenino');
    default: return 'n/d';
    }
};

export const parseLanguage = language => {
    switch (language) {
    case ('es'): return i18n.t('Español');
    case ('en'): return i18n.t('Inglés');
    default: return i18n.t('Español');
    }
};

export const parseLanguageLabel = language => {
    if (availableLanguages.length === 1) return '';
    return ` (${ parseLanguage(language) })`;
};

export const parseLicenseNextStep = step => {
    switch (step) {
    case ('none'): return i18n.t('Ninguno');
    case ('discharged'): return i18n.t('Alta');
    case ('new_control'): return i18n.t('Nuevo control');
    default: return '-';
    }
};

export const parseNotSucceded = reason => {
    switch (reason) {
    case ('PATIENT_NOT_PRESENT'): return i18n.t('El paciente no ingresó a la sala.');
    case ('DOCTOR_NOT_PRESENT'): return i18n.t('El profesional no ingresó a la sala');
    case ('DURATION_ZERO'): return i18n.t('La llamada no tuvo duración al momento de cortar.');
    default: return i18n.t('Desconocida.');
    }
};

export const parseOTError = error => {
    switch (error) {
    case ('OT_CHROME_MICROPHONE_ACQUISITION_ERROR'): return i18n.t('Ocurrió un error al acceder al micrófono desde Chrome');
    case ('OT_CONSTRAINTS_NOT_SATISFIED'): return i18n.t('No fue posible obtener las dimensiones para la imagen de la cámara');
    case ('OT_CUSTOM_PUBLISHER_ERROR'): return i18n.t('Ocurrió un problema local al intentar publicar contenido');
    case ('OT_HARDWARE_UNAVAILABLE'): return i18n.t('Los dispositivos multimedia no se encuentran disponibles');
    case ('OT_MEDIA_ERR_ABORTED'): return i18n.t('La comunicación multimedia fue cancelada');
    case ('OT_NO_DEVICES_FOUND'): return i18n.t('No se encontraron dispositivos para publicar el contenido multimedia.');
    case ('OT_NOT_SUPPORTED'): return i18n.t('El dispositivo o su configuración no soportan el intercambio multimedia');
    case ('OT_TIMEOUT'): return i18n.t('Se agotó el tiempo de espera para la conexión');
    case ('OT_UNABLE_TO_CAPTURE_MEDIA'): return i18n.t('Ocurrió un error al intentar acceder a los dispositivos multimedia');
    case ('OT_USER_MEDIA_ACCESS_DENIED'): return i18n.t('El usuario no ha dado permisos de utilización multimedia');
    default: return `${ i18n.t('Error registrado') }: ${ error }`;
    }
};

export const parsePatientInChargeRelation = relation => {
    const map = {
        parent: i18n.t('Padre/Madre'),
        tutor: i18n.t('Tutor')
    };
    return map[relation] ?? '';
};

export const parsePaymentMethod = (method, coverage = i18n.t('Cobertura')) => {
    switch (method) {
    case ('coverage'): return coverage;
    case ('pin'): return i18n.t('PIN');
    case ('payment'): return i18n.t('Pago');
    case ('interconsult'): return i18n.t('Interconsulta');
    case ('admin_finalized'): return i18n.t('Finalizada por el administrador');
    default: return 'n/d';
    }
};

export const parsePreworkAptitude = value => {
    const map = {
        delayed: i18n.t('Aplazado'),
        no_suitable: i18n.t('No apto'),
        suitable: i18n.t('Apto'),
        suitable_pathology: i18n.t('Apto con patología'),
        suitable_restrictions: i18n.t('Apto con restricciones')
    };
    return map[value] ?? i18n.t('Sin resultado');
};

export const parsePreworkType = value => {
    const map = {
        health_notebook: i18n.t('Libreta sanitaria'),
        other: i18n.t('Otro'),
        periodical: i18n.t('Periódico'),
        position_change: i18n.t('Cambio de puesto'),
        prework: i18n.t('Preempleo'),
        retirement: i18n.t('Salida (Retiro)')
    };
    return map[value] ?? '-';
};

export const parseRecipeRequestStatus = status => {
    const map = {
        accepted: i18n.t('Aceptado'),
        approved: i18n.t('Aprobado'),
        oncourse: i18n.t('En curso'),
        pending: i18n.t('Pendiente'),
        rejected: i18n.t('Rechazado')
    };
    return map[status] ?? i18n.t('Desconocido');
};

export const parseSelect = (list = [], label = 'name', value = 'id') => list.map((elem => ({
    ...elem,
    label: elem[label],
    value: elem[value]
})));

export const parseStringSelect = (list, labelFunc = e => e, valueFunc = e => e) => list.map(elem => ({
    label: labelFunc(elem),
    value: valueFunc(elem)
}));

export const parseUserType = type => {
    switch (type) {
    case ('admin'): return i18n.t('Admin');
    case ('doctor'): return i18n.t('Profesional');
    case ('patient'): return i18n.t('Paciente');
    default: '-';
    }
};

export const removePoint = string => string.replace('.', '');

export const replaceObjectArray = (array, element, key, value) => {
    const newArray = [...array];
    const index = newArray.findIndex(elem => elem[key] === value);
    if (index >= 0) {
        newArray[index] = element;
    }
    return newArray;
};

export const setQueryValue = (query, value) => {
    const url = new URL(window.location.href);
    url.searchParams.set(query, value);
    window.history.replaceState(null, null, url.href);
};

export const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

export const timezoneOffsetString = () => {
    const utcOffset = moment().utcOffset();
    return `${ utcOffset < 0 ? '-' : '+' }${ (Math.abs(utcOffset) / 60).toString().padStart(2, '0') }:${ (utcOffset % 60).toString().padStart(2, '0') }`;
};

export const toggleElementArray = (array, value) => {
    const newArray = [...array];
    const index = newArray.indexOf(value);
    if (index < 0) return [...newArray, value];
    newArray.splice(index, 1);
    return newArray;
};

export const uppercaseFirst = string => {
    if (typeof string === 'string' && string.length > 0) {
        return string[0].toUpperCase() + string.slice(1);
    } else {
        return string;
    }
};

export const validationSchema = schema => {
    const findFirstTrue = array => {
        let i = 0;
        while (i < array.length) {
            if (!array[i][0]) {
                return array[i][1];
            }
            i++;
        }
        return undefined;
    };
    const errors = {};
    Object.keys(schema).forEach(key => {
        const validationType = typeof schema[key][0];
        const error = findFirstTrue(
            validationType === 'boolean'
                ? [schema[key]]
                : validationType === 'object'
                    ? schema[key]
                    : undefined
        );
        !!error && (errors[key] = error);
    });
    const isValid = isEmpty(errors);
    return {
        errors,
        isValid
    };
};
